import React, { useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Modal } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand } from '@fortawesome/free-solid-svg-icons';
import '../../../theme/Layout/ChartConfig';
import {
  ChartCard,
  ChartCardHeader,
  ChartCardTitle,
  ChartCardSubtitle,
  ChartCardActions,
  ExpandButton,
  BreakdownList,
  BreakdownRow,
  BreakdownHeader,
  BreakdownLabel,
  BreakdownValue,
  BreakdownBarTrack,
  BreakdownBarFill,
  DonutRow,
  DonutChartWrapper,
  StyledSelect,
  ModalSubtitle,
} from './Styles';
import { PERIOD_FILTER_OPTIONS } from './Constant';
import PlatformFilterDropdown from './PlatformFilterDropdown';
import type { OrderChannelSplitCardProps } from './types';

const OrderChannelSplitCard: React.FC<OrderChannelSplitCardProps> = ({ data, isDarkMode = false }) => {
  const [period, setPeriod] = useState('monthly');
  const [channel, setChannel] = useState('all');
  const [expanded, setExpanded] = useState(false);

  const chartData = {
    labels: data.map((item) => item.label),
    datasets: [
      {
        data: data.map((item) => item.value),
        backgroundColor: data.map((item) => item.color),
        hoverBackgroundColor: data.map((item) => item.color),
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    cutout: '68%',
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: isDarkMode ? '#1a202c' : '#fff',
        titleColor: isDarkMode ? '#fff' : '#1a202c',
        bodyColor: isDarkMode ? '#fff' : '#1a202c',
        borderColor: isDarkMode ? '#4a5568' : '#e2e8f0',
        borderWidth: 1,
        callbacks: {
          label: (context: any) => `${context.label}: ${context.formattedValue}%`,
        },
      },
    },
  };

  const renderBreakdownList = () => (
    <BreakdownList>
      {data.map((item) => (
        <BreakdownRow key={item.label}>
          <BreakdownHeader>
            <BreakdownLabel color={item.color}>{item.label}</BreakdownLabel>
            <BreakdownValue>{item.value}%</BreakdownValue>
          </BreakdownHeader>
          <BreakdownBarTrack>
            <BreakdownBarFill percent={item.value} color={item.color} />
          </BreakdownBarTrack>
        </BreakdownRow>
      ))}
    </BreakdownList>
  );

  return (
    <ChartCard>
      <ChartCardHeader>
        <div>
          <ChartCardTitle>Order Channels</ChartCardTitle>
          <ChartCardSubtitle>Where this month's orders came from</ChartCardSubtitle>
        </div>
        <ChartCardActions>
          <ExpandButton aria-label="Expand chart" onClick={() => setExpanded(true)}>
            <FontAwesomeIcon icon={faExpand} />
          </ExpandButton>
          <PlatformFilterDropdown value={channel} onChange={setChannel} />
          <StyledSelect
            value={period}
            onChange={setPeriod}
            options={PERIOD_FILTER_OPTIONS}
            dropdownStyle={{ borderRadius: 12, overflow: 'hidden' }}
          />
        </ChartCardActions>
      </ChartCardHeader>

      {renderBreakdownList()}

      <Modal open={expanded} onCancel={() => setExpanded(false)} footer={null} title="Order Channels" width={720}>
        <ModalSubtitle>Where this month's orders came from</ModalSubtitle>
        <DonutRow>
          <DonutChartWrapper style={{ width: 280, height: 280 }}>
            <Doughnut data={chartData} options={chartOptions} />
          </DonutChartWrapper>
          {renderBreakdownList()}
        </DonutRow>
      </Modal>
    </ChartCard>
  );
};

export default OrderChannelSplitCard;
